import { useRef, useState, useCallback } from 'react';
import {
  Stack,
  Group,
  Button,
  Select,
  Text,
  Paper,
  ThemeIcon,
  Alert,
  useMantineTheme,
} from '@mantine/core';
import {
  IconUpload,
  IconFileSpreadsheet,
  IconX,
  IconCheck,
  IconInfoCircle,
} from '@tabler/icons-react';
import type { ImportWizardState } from '../types';
import { parseSpreadsheet } from '../utils/parseSpreadsheet';
import { LIVESTOCK_TYPE_OPTIONS, CURRENCIES } from '@/types/livestock';

const ACCEPTED_EXTENSIONS = ['.xlsx', '.xls', '.csv'];
const ACCEPTED_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'text/csv',
];

interface UploadStepProps {
  state: ImportWizardState;
  onUpdate: (updates: Partial<ImportWizardState>) => void;
  onNext: () => void;
}

export function UploadStep({ state, onUpdate, onNext }: UploadStepProps) {
  const theme = useMantineTheme();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const isValidFile = (file: File): boolean => {
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    return (
      ACCEPTED_EXTENSIONS.includes(ext) ||
      ACCEPTED_MIME_TYPES.includes(file.type)
    );
  };

  const handleFile = useCallback(
    async (file: File) => {
      if (!isValidFile(file)) {
        setParseError(
          'Invalid file type. Please upload an .xlsx, .xls, or .csv file.',
        );
        return;
      }

      setIsParsing(true);
      setParseError(null);

      try {
        const parsed = await parseSpreadsheet(file);
        onUpdate({
          parsedData: parsed,
          fileName: file.name,
        });
      } catch (err) {
        setParseError(
          err instanceof Error ? err.message : 'Failed to parse file',
        );
        onUpdate({ parsedData: null, fileName: null });
      } finally {
        setIsParsing(false);
      }
    },
    [onUpdate],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      const file = e.dataTransfer.files[0];
      if (file) {
        void handleFile(file);
      }
    },
    [handleFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        void handleFile(file);
      }
      // Reset input so the same file can be re-selected
      e.target.value = '';
    },
    [handleFile],
  );

  const handleRemoveFile = useCallback(() => {
    onUpdate({ parsedData: null, fileName: null });
    setParseError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [onUpdate]);

  const hasFile = !!state.parsedData;
  const canProceed = hasFile && !isParsing;

  return (
    <Stack gap="lg" mt="lg">
      {/* Drag-and-drop zone */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_EXTENSIONS.join(',')}
        onChange={handleInputChange}
        style={{ display: 'none' }}
      />

      {!hasFile ? (
        <Paper
          withBorder
          p="xl"
          style={{
            borderStyle: 'dashed',
            borderWidth: 2,
            borderColor: isDragOver
              ? theme.colors.blue[5]
              : parseError
                ? theme.colors.red[5]
                : undefined,
            backgroundColor: isDragOver
              ? theme.colors.blue[0]
              : undefined,
            cursor: 'pointer',
            transition: 'border-color 150ms, background-color 150ms',
          }}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <Stack align="center" gap="md" py="xl">
            <ThemeIcon size={48} variant="light" color={parseError ? 'red' : 'blue'} radius="xl">
              {parseError ? <IconX size={28} /> : <IconUpload size={28} />}
            </ThemeIcon>
            {isParsing ? (
              <Text size="lg" fw={500}>
                Parsing file...
              </Text>
            ) : parseError ? (
              <>
                <Text size="lg" fw={500} c="red">
                  {parseError}
                </Text>
                <Text size="sm" c="dimmed">
                  Click or drag a new file to try again
                </Text>
              </>
            ) : (
              <>
                <Text size="lg" fw={500}>
                  Drag & drop a spreadsheet here, or click to browse
                </Text>
                <Text size="sm" c="dimmed">
                  Accepted formats: .xlsx, .xls, .csv
                </Text>
              </>
            )}
          </Stack>
        </Paper>
      ) : (
        <Paper withBorder p="md">
          <Group justify="space-between">
            <Group gap="sm">
              <ThemeIcon size={36} variant="light" color="green" radius="xl">
                <IconFileSpreadsheet size={20} />
              </ThemeIcon>
              <Stack gap={2}>
                <Group gap="xs">
                  <IconCheck size={14} color={theme.colors.green[6]} />
                  <Text fw={500}>{state.fileName}</Text>
                </Group>
                <Text size="sm" c="dimmed">
                  {state.parsedData!.rows.length} rows,{' '}
                  {state.parsedData!.headers.length} columns
                </Text>
              </Stack>
            </Group>
            <Button
              variant="subtle"
              color="red"
              size="compact-sm"
              onClick={handleRemoveFile}
            >
              Remove
            </Button>
          </Group>
        </Paper>
      )}

      {/* Info banner for multi-sheet workbooks */}
      {hasFile && state.parsedData!.sheetNames.length > 1 && (
        <Alert
          icon={<IconInfoCircle size={16} />}
          color="blue"
          variant="light"
        >
          This workbook has multiple sheets. Only the first sheet will be
          imported: <strong>{state.parsedData!.selectedSheet}</strong>.
        </Alert>
      )}

      {/* Type and currency selectors */}
      <Group gap="md">
        <Select
          label="Livestock type"
          description="Type to assign to all imported rows"
          data={LIVESTOCK_TYPE_OPTIONS}
          value={state.livestockType}
          onChange={(val) => {
            if (val) {
              onUpdate({
                livestockType: val as ImportWizardState['livestockType'],
              });
            }
          }}
          style={{ width: 200 }}
          allowDeselect={false}
        />
        <Select
          label="Currency"
          description="Currency for price values"
          data={CURRENCIES.map((c) => ({
            value: c.code,
            label: `${c.symbol} ${c.label}`,
          }))}
          value={state.currency}
          onChange={(val) => {
            if (val) {
              onUpdate({
                currency: val as ImportWizardState['currency'],
              });
            }
          }}
          style={{ width: 220 }}
          allowDeselect={false}
        />
      </Group>

      {/* Next button */}
      <Group justify="flex-end" mt="md">
        <Button onClick={onNext} disabled={!canProceed} size="md">
          Next: Map Columns
        </Button>
      </Group>
    </Stack>
  );
}
